// src/components/Receipt.js
// ─────────────────────────────────────────────────────────────
//  Receipt component — thermal print + QR + email
//  Used in BillingPage after payment confirmed
//  Also rendered as standalone printable page
// ─────────────────────────────────────────────────────────────

import React, { useRef } from 'react';

const TAX_RATE = 0.05;

// ── QR Code (inline SVG — no library needed) ──────────────────
// Generates a simple QR-like placeholder; replace with
// qrcode.react library for real QR codes
function QRCode({ value, size = 80 }) {
  // Use a free QR API for simplicity
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}`;
  return (
    <img
      src={url}
      alt="QR Code"
      width={size}
      height={size}
      style={{ display: 'block', margin: '0 auto', imageRendering: 'pixelated' }}
    />
  );
}

export default function Receipt({ order, vendor, onClose }) {
  const printRef = useRef();

  if (!order) return null;

  const subtotal  = order.subtotal || order.items?.reduce((s, i) => s + i.price * (i.quantity || i.qty), 0) || 0;
  const tax       = order.tax || subtotal * TAX_RATE;
  const discount  = order.discount || 0;
  const total     = order.total || subtotal + tax - discount;
  const receiptNo = `RCP-${order.orderNumber || order.id?.slice(0, 8).toUpperCase()}`;
  const qrData    = JSON.stringify({ receipt: receiptNo, total, date: new Date().toISOString() });

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open('', '_blank', 'width=400,height=600');
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt ${receiptNo}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            color: #000;
            background: #fff;
            padding: 10px;
            width: 80mm;
          }
          .center  { text-align: center; }
          .bold    { font-weight: bold; }
          .large   { font-size: 16px; }
          .xlarge  { font-size: 20px; }
          .divider { border-top: 1px dashed #000; margin: 8px 0; }
          .row     { display: flex; justify-content: space-between; margin-bottom: 4px; }
          .total   { font-size: 16px; font-weight: bold; }
          img      { display: block; margin: 8px auto; }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>${content}</body>
      </html>
    `);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 300);
  };

  const handleEmail = () => {
    // In production: call /api/billing/email-receipt
    alert('Email receipt feature coming in Phase 3!');
  };

  // ── Receipt content (used for both display and printing) ──
  const ReceiptContent = () => (
    <div ref={printRef} style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: '#000', background: '#fff', padding: 20, maxWidth: 320, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>⚡ QuickServe Kiosk</div>
        {vendor?.name && <div style={{ fontSize: 14, marginTop: 4 }}>{vendor.name}</div>}
        {vendor?.mallId && <div style={{ fontSize: 11, color: '#555' }}>{vendor.mallId}</div>}
        <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>
          {new Date().toLocaleString('en', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          })}
        </div>
      </div>

      {/* Receipt & Order number */}
      <div style={{ borderTop: '1px dashed #000', borderBottom: '1px dashed #000', padding: '8px 0', marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span>Receipt:</span><span style={{ fontWeight: 700 }}>{receiptNo}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
          <span>Order:</span><span style={{ fontWeight: 700 }}>{order.orderNumber}</span>
        </div>
        {order.tableNo && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
            <span>Table:</span><span>{order.tableNo}</span>
          </div>
        )}
      </div>

      {/* Items */}
      <div style={{ marginBottom: 10 }}>
        {order.items?.map((item, i) => {
          const qty = item.quantity || item.qty || 1;
          return (
            <div key={i} style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>{item.name}</span>
                <span>AED {(item.price * qty).toFixed(2)}</span>
              </div>
              <div style={{ fontSize: 11, color: '#555' }}>
                {qty} × AED {item.price.toFixed(2)}
                {item.modifierNote ? ` — ${item.modifierNote}` : ''}
              </div>
            </div>
          );
        })}
      </div>

      {/* Totals */}
      <div style={{ borderTop: '1px dashed #000', paddingTop: 8, marginBottom: 10 }}>
        {[
          ['Subtotal', `AED ${subtotal.toFixed(2)}`],
          ['VAT (5%)', `AED ${tax.toFixed(2)}`],
          ...(discount > 0 ? [['Discount', `- AED ${discount.toFixed(2)}`]] : []),
        ].map(([l, v]) => (
          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4, color: l === 'Discount' ? 'green' : '#000' }}>
            <span>{l}</span><span>{v}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16, marginTop: 8, paddingTop: 8, borderTop: '1px dashed #000' }}>
          <span>TOTAL</span><span>AED {total.toFixed(2)}</span>
        </div>
      </div>

      {/* Payment method */}
      <div style={{ fontSize: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Payment:</span>
          <span style={{ fontWeight: 600 }}>{order.paymentMode || 'CASH'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span>Status:</span>
          <span style={{ fontWeight: 600, color: order.paymentStatus === 'PAID' ? 'green' : '#000' }}>
            {order.paymentStatus === 'PAID' ? '✓ PAID' : 'PENDING'}
          </span>
        </div>
      </div>

      {/* QR Code */}
      <div style={{ borderTop: '1px dashed #000', paddingTop: 12, textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 8 }}>
          Scan to verify receipt
        </div>
        <QRCode value={qrData} size={80} />
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: '#555' }}>
        <div>Thank you for your order!</div>
        <div style={{ marginTop: 4 }}>Powered by QuickServe Kiosk</div>
      </div>
    </div>
  );

  // ── Modal wrapper ─────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 16,
        padding: 0, maxWidth: 420, width: '95vw',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,.5)',
      }}>
        {/* Action bar */}
        <div style={{
          display: 'flex', gap: 8, padding: 16,
          borderBottom: '1px solid #eee',
          background: '#f8f8f8',
          borderRadius: '16px 16px 0 0',
        }}>
          <button onClick={handlePrint} style={{
            flex: 1, padding: '10px 0', borderRadius: 8,
            background: '#000', color: '#fff',
            border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}>
            🖨 Print Receipt
          </button>
          <button onClick={handleEmail} style={{
            flex: 1, padding: '10px 0', borderRadius: 8,
            background: '#1a73e8', color: '#fff',
            border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}>
            📧 Email Receipt
          </button>
          <button onClick={onClose} style={{
            padding: '10px 14px', borderRadius: 8,
            background: '#eee', color: '#333',
            border: 'none', fontWeight: 700, fontSize: 18, cursor: 'pointer',
          }}>
            ✕
          </button>
        </div>

        {/* Receipt preview */}
        <div style={{ padding: '16px 16px 24px' }}>
          <ReceiptContent />
        </div>
      </div>
    </div>
  );
}
